import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';

import { alpha } from '@material-ui/core/styles';
import { statusColors, brand, radius, surface } from './tokens';
import './style.css';

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).map(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#172B4D',
  };
});

export const htecTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: brand.primary,
        light: '#4C9AFF',
        dark: '#172B4D',
      },
      secondary: {
        main: brand.secondary,
        light: '#FFAB00',
        dark: '#6554C0',
      },
      grey: {
        50: '#C1C7D0',
        100: '#7A869A',
        200: '#6B778C',
        300: '#5E6C84',
        400: '#505F79',
        500: '#42526E',
        600: '#344563',
        700: '#253858',
        800: '#172B4D',
        900: '#091E42',
      },
      error: {
        main: '#C6433E',
        light: '#ECACA9',
        dark: '#772825',
      },
      warning: {
        main: '#BA9C3C',
        light: '#F8E8B3',
        dark: '#8A742C',
      },
      success: {
        main: '#5B7F4D',
        light: '#C0DEB5',
        dark: '#46623B',
      },
      info: {
        main: '#1848B3',
        light: '#DFE6F6',
        dark: '#133A8F',
      },
      highlight: brand.highlight,
      navigation: {
        ...palettes.light.navigation,
        background: '#F8F9FA',
        color: '#08090D',
        indicator: 'transparent',
        selectedColor: '#2A5A4F',

        navItem: {
          hoverBackground: '#F5F7F9',
          hoverColor: '#2A5A4F',
        },
      },
      text: {
        primary: '#172B48',
      },
      background: {
        default: '#F8F9FA',
        paper: '#F8F9FA',
      },
    },
  }),
  typography: {
    htmlFontSize: 16,
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontSize: 54,
      fontWeight: 700,
      marginBottom: 10,
    },
    h2: {
      fontSize: 40,
      fontWeight: 700,
      marginBottom: 8,
    },
    h3: {
      fontSize: 32,
      fontWeight: 700,
      marginBottom: 6,
    },
    h4: {
      fontWeight: 700,
      fontSize: 28,
      marginBottom: 6,
    },
    h5: {
      fontWeight: 700,
      fontSize: 24,
      marginBottom: 4,
    },
    h6: {
      fontWeight: 700,
      fontSize: 20,
      marginBottom: 2,
    },
  },
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    BackstageContent: {
      styleOverrides: {
        root: {},
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme: _theme }) => ({
          backgroundImage: 'unset',
          backgroundColor: '#FFFFFF !important',
          boxShadow: '0px 3.5px 5.5px 0px rgba(0, 0, 0, 0.02) !important',
          padding: '24px 32px !important',
          borderRadius: radius.xl,
          margin: '20px 20px !important',
          border: '1px solid #E2E8F0',
          minHeight: '100px !important',
          display: 'flex !important',
          width: 'auto !important',
          alignItems: 'center !important',
        }),
        title: ({ theme: _theme }) => ({
          fontFamily: 'Inter, sans-serif !important',
          color: '#2D3748 !important',
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.4,
          display: 'block',
        }),
        subtitle: ({ theme: _theme }) => ({
          fontFamily: 'Inter, sans-serif !important',
          color: '#A0AEC0 !important',
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1.5,
          display: 'block',
          marginTop: 4,
        }),
        type: ({ theme }) => ({
          color: alpha(theme.page.fontColor, 0.8),
        }),
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontSize: 'inherit',
          textTransform: 'none',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          '& .techdocs-reader-page': {
            backgroundColor: '#FFF',
            borderRadius: radius.xl,
            borderColor: '#E2E8F0',
            borderWidth: '1px',
            borderStyle: 'solid',
            boxShadow: '0px 3.5px 5.5px 0px rgba(0,0,0,0.02)',
            // margin: '5px 0px 30px 0px',
            padding: '70px 30px',
          },
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        selected: {
          backgroundColor: '#FFFFFF !important',
          color: '#2D3748 !important',
          boxShadow: '0px 3.5px 5.5px 0px rgba(0,0,0,0.02) !important',
          '& div[class*="MuiBox-root"]': {
            backgroundColor: '#2A5A4F !important',
            '& svg': {
              color: '#FFFFFF !important',
            },
          },
        },
        iconContainer: {
          backgroundColor: '#FFFFFF !important',
          width: '30px !important',
          height: '30px !important',
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          marginRight: '12px !important',
          borderRadius: radius.lg,
          minWidth: '30px !important',
          boxShadow: '0px 3.5px 5.5px 0px rgba(0, 0, 0, 0.02) !important',

          '& svg': {
            width: '15px !important',
            height: '15px !important',
            color: '#2A5A4F !important',
          },
        },
        root: {
          backgroundColor: 'transparent',
          fontSize: '12px',
          width: '95% !important',
          fontWeight: '700',
          color: '#A0AEC0',
          margin: '3px 8px !important',
          borderRadius: radius.xl,
          padding: '12px 16px !important',
          minHeight: '54px',

          '&:hover': {
            color: '#2D3748 !important',
            backgroundColor: 'rgba(255, 255, 255, 0.47) !important',
          },

          // Search and Settings special styling
          '.htec-sidebar-search &': {
            minHeight: '30px',
            padding: 0,
          },
        },
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: {
          margin: '16px 0',
          borderBottom: '1px solid #E0E1E2',
          backgroundColor: 'red',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          '&[aria-expanded=true]': {},
        },
      },
    },
    BackstageSignInPage: {
      styleOverrides: {
        item: {
          '& [class*="MuiCard-root"], & [class*="MuiPaper-root"]': {
            backgroundColor: '#FFFFFF',
            borderRadius: radius.xl,
            boxShadow: '0px 7px 23px 0px rgba(0, 0, 0, 0.05)',
            minHeight: '261px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
          },
          '& [class*="MuiCardHeader-root"]': {
            padding: '24px 24px 16px 24px',
          },
          '& [class*="MuiCardHeader-title"]': {
            fontSize: '18px',
            fontWeight: 700,
            color: '#2D3748',
          },
          '& [class*="MuiCardContent-root"] p': {
            fontSize: '14px',
            lineHeight: 1.6,
            color: '#718096',
          },
          '& [class*="MuiCardActions-root"]': {
            padding: '16px 24px 24px 24px',
          },
          '& [class*="MuiButton-root"]': {
            borderRadius: radius.md,
            fontSize: '14px',
            fontWeight: 700,
            textTransform: 'none',
            padding: '10px 24px',
            backgroundColor: brand.primary,
            color: '#FFFFFF',
          },
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundImage: 'unset !important',
        },
      },
    },
    BackstageOpenedDropdown: {
      styleOverrides: {
        icon: {
          '& path': {
            fill: '#FFFFFF',
          },
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '&> :first-child': {
            borderBottom: '1px solid #D5D5D5',
            boxShadow: 'none',
          },
          '& th': {
            borderTop: 'none',
            textTransform: 'none !important',
          },
        },
      },
    },
    CatalogReactUserListPicker: {
      styleOverrides: {
        title: {
          textTransform: 'none',
        },
        root: {
          borderRadius: radius.md,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          boxShadow: '2px 1px 5px 0px rgba(0, 0, 0, 0.05)',
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'none',
          padding: '15px 10px',
        },
      },
    },

    BackstageSelect: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          boxShadow: '2px 1px 5px 0px rgba(0, 0, 0, 0.05)',
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'none',
          padding: '15px 10px',
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          padding: '24px 24px 16px 24px',
        },
        title: {
          '& h2, & [class*="MuiTypography-h5"]': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 700,
            color: '#2D3748',
            lineHeight: 1.4,
          },
        },
        searchField: {
          '& [class*="MuiInput-root"]': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
          },
        },
      },
    },
    BackstageSubvalueCell: {
      styleOverrides: {
        value: {
          '& a, & [class*="MuiLink-root"]': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 700,
            color: '#2D3748',
            textDecoration: 'underline',
            textDecorationSkipInk: 'none',
            lineHeight: 1.4,
          },
        },
        subvalue: {
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          color: '#718096',
          lineHeight: 1.4,
          marginTop: '4px',
        },
      },
    },
    CatalogReactEntityDisplayName: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          fontWeight: 700,
          '& a, & [class*="MuiLink-root"]': {
            color: '#2D3748',
            textDecoration: 'underline',
            textDecorationSkipInk: 'none',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.none,
        },
        // Standard alerts map each severity onto its own semantic tier
        // (bg / border / text / icon) from the Status Color Guidelines.
        standardError: {
          color: statusColors.error.text,
          backgroundColor: statusColors.error.bg,
          border: `1px solid ${statusColors.error.border}`,
          '& $icon': {
            color: statusColors.error.icon,
          },
        },
        standardInfo: {
          color: statusColors.info.text,
          backgroundColor: statusColors.info.bg,
          border: `1px solid ${statusColors.info.border}`,
          '& $icon': {
            color: statusColors.info.icon,
          },
        },
        standardSuccess: {
          color: statusColors.success.text,
          backgroundColor: statusColors.success.bg,
          border: `1px solid ${statusColors.success.border}`,
          '& $icon': {
            color: statusColors.success.icon,
          },
        },
        standardWarning: {
          color: statusColors.warning.text,
          backgroundColor: statusColors.warning.bg,
          border: `1px solid ${statusColors.warning.border}`,
          '& $icon': {
            color: statusColors.warning.icon,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          boxShadow: '2px 1px 5px 0px rgba(0, 0, 0, 0.05)',
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'none',
          padding: '15px 10px',

          //  '&[aria-expanded=true]': {
          //   backgroundColor: '#26385A',
          //   color: '#26385A',
          //   paddingTop:40,
          // },
          // '&[aria-expanded=true] path': {
          //   fill: '#26385A',
          // },
        },
        inputRoot: {
          // paddingTop:40
        },
        hasClearIcon: {
          //  paddingTop:40
        },
        clearIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
          visibility: 'visible',
          //  top: theme.spacing(2),
        }),
        popupIndicator: {
          color: `${brand.primary} !important`,
          // top: '40px',
        },

        endAdornment: () => ({
          // top: 0,
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9,30,69,0.54)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          textTransform: 'none',
        },
        contained: {
          boxShadow: 'none',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '& [class*="MuiIconButton-label"]': {
            color: brand.primary,
          },
          '& svg': {
            width: '18px',
            height: '18px',
          },
        },
      },
    },
    // Single source of truth for chip/tag styling. (The former duplicate
    // definition under MuiPaper '&.MuiChip-root' has been removed.)
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          borderColor: brand.highlight,
          borderWidth: '1px',
          borderStyle: 'solid',
          backgroundColor: 'transparent',
          color: brand.highlight,
          margin: 4,
          height: '20px',
          display: 'inline-table',
          padding: '4px 12px',
          alignItems: 'center',
          textTransform: 'none',
        },
        outlined: {
          color: brand.highlight,
          border: `1px solid ${brand.highlight}`,
          height: '20px',
          display: 'inline-block',
          padding: '4px 12px',
          fontSize: '10px',
          alignItems: 'center',
          fontWeight: 700,
          borderRadius: radius.lg,
          textTransform: 'none',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&[aria-expanded]': {
            backgroundColor: '#26385A',
            color: '#FFFFFF',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 10,
        },
        switchBase: {
          padding: 12,
        },
        thumb: {
          backgroundColor: '#FFFFFF',
          height: 14,
          width: 14,
        },
        track: {
          borderRadius: 9,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {},
        scroller: {
          backgroundColor: surface.background,
          borderRadius: radius.xl,
          boxShadow: surface.shadow,
          border: surface.border,
        },
        root: {
          '& .MuiTabs-flexContainer': {
            // backgroundColor:'#0c0cfeff',
          },
        },
        indicator: {
          transition: 'none',
          backgroundColor: brand.primary,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        button: {
          textTransform: 'none',
        },
        h5: {
          fontFamily: 'Inter, sans-serif',
          fontSize: '18px',
          fontWeight: 700,
          color: '#2D3748',
          lineHeight: 1.4,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          '& [class*="MuiTypography-h5"]': {
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            fontWeight: 700,
            color: '#2D3748',
            lineHeight: 1.4,
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        action: {
          // targets the right-side action area buttons/links
          '& .MuiIconButton-root': {
            color: '#f70b0bff',
            // example extras:
            // padding: 4,
            // borderRadius: 8,
          },

          '& .MuiIconButton-root:hover': {
            // backgroundColor: '#eee',
          },

          '& .MuiIconButton-root.Mui-disabled': {
            opacity: 0.4,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: surface.background,
          boxShadow: surface.shadow,
          border: surface.border,
        },
        elevation1: {
          // optional: shadow style, etc.
        },
        elevation2: {
          backgroundColor: surface.background,
          borderRadius: radius.xl,
          border: surface.border,
          boxShadow: surface.shadow,
        },
        rounded: {
          borderRadius: radius.xl,
        },
      },
    },
  },
});
